import { cacheService } from './cacheService';
import { 
  patientService, 
  practitionerService, 
  prescriptionService, 
  priorAuthService,
  insuranceService,
  dashboardService,
  payerService
} from './api';
import { 
  Patient, 
  PatientDetailResponse, 
  Practitioner, 
  Prescription, 
  PriorAuthorization,
  Insurance
} from '../types';

/**
 * Cached version of the API services
 * This service checks the cache before making API calls
 * and stores the results in the cache after receiving them
 */

// Cache expiration times (in milliseconds)
const CACHE_TIMES = {
  PATIENT_LIST: 5 * 60 * 1000, // 5 minutes
  PATIENT_DETAIL: 5 * 60 * 1000, // 5 minutes
  PRACTITIONER_LIST: 10 * 60 * 1000, // 10 minutes
  PRACTITIONER_DETAIL: 10 * 60 * 1000, // 10 minutes
  PRESCRIPTION_LIST: 5 * 60 * 1000, // 5 minutes
  PRIOR_AUTH_LIST: 5 * 60 * 1000, // 5 minutes
  INSURANCE_LIST: 30 * 60 * 1000, // 30 minutes
  DASHBOARD_STATS: 2 * 60 * 1000, // 2 minutes
};

export const cachedPatientService = {
  getAllPatients: async (): Promise<Patient[]> => {
    const cacheKey = 'patients_all';
    const cachedData = cacheService.get<Patient[]>(cacheKey);
    
    if (cachedData) {
      console.log('Using cached patient list');
      return cachedData;
    }
    
    const data = await patientService.getAllPatients();
    cacheService.set(cacheKey, data, CACHE_TIMES.PATIENT_LIST);
    return data;
  },
  
  getPatientDetail: async (patientId: string): Promise<PatientDetailResponse> => {
    const cacheKey = `patient_${patientId}`;
    const cachedData = cacheService.get<PatientDetailResponse>(cacheKey);
    
    if (cachedData) {
      console.log(`Using cached patient detail for ${patientId}`);
      return cachedData;
    }
    
    const data = await patientService.getPatientDetail(patientId);
    cacheService.set(cacheKey, data, CACHE_TIMES.PATIENT_DETAIL);
    return data;
  },
  
  getAssignedPatients: async (providerEmail: string): Promise<string[]> => {
    const cacheKey = `assigned_patients_${providerEmail}`;
    const cachedData = cacheService.get<string[]>(cacheKey);
    
    if (cachedData) {
      console.log(`Using cached assigned patients for ${providerEmail}`);
      return cachedData;
    }
    
    const data = await patientService.getAssignedPatients(providerEmail);
    cacheService.set(cacheKey, data, CACHE_TIMES.PATIENT_LIST);
    return data;
  },
  
  deletePatient: async (patientId: string): Promise<void> => {
    // Delete from API
    await patientService.deletePatient(patientId);
    
    // Clear related caches
    cacheService.remove(`patient_${patientId}`);
    cacheService.remove('patients_all');
  },
};

export const cachedPractitionerService = {
  getAllPractitioners: async (): Promise<Practitioner[]> => {
    const cacheKey = 'practitioners_all';
    const cachedData = cacheService.get<Practitioner[]>(cacheKey);
    
    if (cachedData) {
      console.log('Using cached practitioner list');
      return cachedData;
    }
    
    const data = await practitionerService.getAllPractitioners();
    cacheService.set(cacheKey, data, CACHE_TIMES.PRACTITIONER_LIST);
    return data;
  },
  
  getPractitionerDetail: async (practitionerId: number): Promise<Practitioner> => {
    const cacheKey = `practitioner_${practitionerId}`;
    const cachedData = cacheService.get<Practitioner>(cacheKey);
    
    if (cachedData) {
      console.log(`Using cached practitioner detail for ${practitionerId}`);
      return cachedData;
    }
    
    const data = await practitionerService.getPractitionerDetail(practitionerId);
    cacheService.set(cacheKey, data, CACHE_TIMES.PRACTITIONER_DETAIL);
    return data;
  },
};

export const cachedPrescriptionService = {
  getAllPrescriptions: async (): Promise<Prescription[]> => {
    const cacheKey = 'prescriptions_all';
    const cachedData = cacheService.get<Prescription[]>(cacheKey);
    
    if (cachedData) {
      console.log('Using cached prescription list');
      return cachedData;
    }
    
    const data = await prescriptionService.getAllPrescriptions();
    cacheService.set(cacheKey, data, CACHE_TIMES.PRESCRIPTION_LIST);
    return data;
  },
  
  getPatientPrescriptions: async (patientId: string): Promise<Prescription[]> => {
    const cacheKey = `prescriptions_patient_${patientId}`;
    const cachedData = cacheService.get<Prescription[]>(cacheKey);
    
    if (cachedData) {
      console.log(`Using cached prescriptions for patient ${patientId}`);
      return cachedData;
    }
    
    const data = await prescriptionService.getPatientPrescriptions(patientId);
    cacheService.set(cacheKey, data, CACHE_TIMES.PRESCRIPTION_LIST);
    return data;
  },
};

export const cachedPriorAuthService = {
  getAllPriorAuthorizations: async (): Promise<PriorAuthorization[]> => {
    const cacheKey = 'prior_auths_all';
    const cachedData = cacheService.get<PriorAuthorization[]>(cacheKey);
    
    if (cachedData) {
      console.log('Using cached prior authorization list');
      return cachedData;
    }
    
    const data = await priorAuthService.getAllPriorAuthorizations();
    cacheService.set(cacheKey, data, CACHE_TIMES.PRIOR_AUTH_LIST);
    return data;
  },
  
  getPatientPriorAuthorizations: async (patientId: string): Promise<PriorAuthorization[]> => {
    const cacheKey = `prior_auths_patient_${patientId}`;
    const cachedData = cacheService.get<PriorAuthorization[]>(cacheKey);
    
    if (cachedData) {
      console.log(`Using cached prior authorizations for patient ${patientId}`);
      return cachedData;
    }
    
    const data = await priorAuthService.getPatientPriorAuthorizations(patientId);
    cacheService.set(cacheKey, data, CACHE_TIMES.PRIOR_AUTH_LIST);
    return data;
  },
  
  deletePriorAuth: async (id: number): Promise<void> => {
    await priorAuthService.deletePriorAuth(id);
    
    // Clear related caches
    cacheService.remove('prior_auths_all');
    // We don't know which patient this belongs to, so we can't clear specific patient cache
    // Instead, we'll clear all patient-specific prior auth caches
    Object.keys(localStorage)
      .filter(key => key.startsWith('cache_prior_auths_patient_'))
      .forEach(key => localStorage.removeItem(key));
  },
  
  createPriorAuth: async (priorAuth: Partial<PriorAuthorization>): Promise<PriorAuthorization> => {
    const data = await priorAuthService.createPriorAuth(priorAuth);
    
    // Clear related caches
    cacheService.remove('prior_auths_all');
    if (priorAuth.patient) {
      cacheService.remove(`prior_auths_patient_${priorAuth.patient}`);
      cacheService.remove(`patient_${priorAuth.patient}`); // Clear patient detail cache as well
    }
    
    return data;
  },
  
  updatePriorAuthStatus: async (id: number, status: 'approved' | 'rejected', rejectionReason?: string): Promise<PriorAuthorization> => {
    const data = await priorAuthService.updatePriorAuthStatus(id, status, rejectionReason);
    
    // Clear related caches
    cacheService.remove('prior_auths_all');
    // We don't know which patient this belongs to, so we can't clear specific patient cache
    // Instead, we'll clear all patient-specific prior auth caches
    Object.keys(localStorage)
      .filter(key => key.startsWith('cache_prior_auths_patient_'))
      .forEach(key => localStorage.removeItem(key));
    
    return data;
  },
};

export const cachedInsuranceService = {
  getAllInsurance: async (): Promise<Insurance[]> => {
    const cacheKey = 'insurance_all';
    const cachedData = cacheService.get<Insurance[]>(cacheKey);
    
    if (cachedData) {
      console.log('Using cached insurance list');
      return cachedData;
    }
    
    const data = await insuranceService.getAllInsurance();
    cacheService.set(cacheKey, data, CACHE_TIMES.INSURANCE_LIST);
    return data;
  },
};

export const cachedDashboardService = {
  getDashboardStats: async () => {
    const cacheKey = 'dashboard_stats';
    const cachedData = cacheService.get(cacheKey);
    
    if (cachedData) {
      console.log('Using cached dashboard stats');
      return cachedData;
    }
    
    const data = await dashboardService.getDashboardStats();
    cacheService.set(cacheKey, data, CACHE_TIMES.DASHBOARD_STATS);
    return data;
  },
};

export const cachedPayerService = {
  getPendingAuthorizations: async (): Promise<PriorAuthorization[]> => {
    const cacheKey = 'payer_pending_auths';
    const cachedData = cacheService.get<PriorAuthorization[]>(cacheKey);
    
    if (cachedData) {
      console.log('Using cached pending authorizations');
      return cachedData;
    }
    
    const data = await payerService.getPendingAuthorizations();
    cacheService.set(cacheKey, data, CACHE_TIMES.PRIOR_AUTH_LIST);
    return data;
  },
  
  getReviewedAuthorizations: async (): Promise<PriorAuthorization[]> => {
    const cacheKey = 'payer_reviewed_auths';
    const cachedData = cacheService.get<PriorAuthorization[]>(cacheKey);
    
    if (cachedData) {
      console.log('Using cached reviewed authorizations');
      return cachedData;
    }
    
    const data = await payerService.getReviewedAuthorizations();
    cacheService.set(cacheKey, data, CACHE_TIMES.PRIOR_AUTH_LIST);
    return data;
  },
  
  reviewAuthorization: async (id: number, status: 'approved' | 'rejected', rejectionReason?: string): Promise<PriorAuthorization> => {
    const data = await payerService.reviewAuthorization(id, status, rejectionReason);
    
    // Clear related caches
    cacheService.remove('payer_pending_auths');
    cacheService.remove('payer_reviewed_auths');
    
    return data;
  },
  
  updateEPAStatus: async (insuranceId: number, epaEnabled: boolean): Promise<any> => {
    const data = await payerService.updateEPAStatus(insuranceId, epaEnabled);
    
    // Clear related caches
    cacheService.remove('insurance_all');
    
    return data;
  },
  
  getInsurancePlans: async (): Promise<Insurance[]> => {
    const cacheKey = 'payer_insurance_plans';
    const cachedData = cacheService.get<Insurance[]>(cacheKey);
    
    if (cachedData) {
      console.log('Using cached insurance plans');
      return cachedData;
    }
    
    const data = await payerService.getInsurancePlans();
    cacheService.set(cacheKey, data, CACHE_TIMES.INSURANCE_LIST);
    return data;
  },
};